import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommunityVideo } from '../../../api/community/types';
import { getImageUrl } from '../../../utils/imageUrl';
import CommunityActionMenu from './CommunityActionMenu';
import './LevelUpCard.css';

interface LevelUpCardProps {
  video: CommunityVideo;
  onEdit?: (video: CommunityVideo) => void;
  onPublish?: (video: CommunityVideo) => void;
  onUnpublish?: (video: CommunityVideo) => void;
  onDelete?: (video: CommunityVideo) => void;
  isMenuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
}

const LevelUpCard: React.FC<LevelUpCardProps> = ({
  video,
  onEdit,
  onPublish,
  onUnpublish,
  onDelete,
  isMenuOpen,
  onToggleMenu,
  onCloseMenu
}) => {
  const navigate = useNavigate();
  const thumbnailUrl = video.thumbnailUrl || video.contentUrl;
  const imageUrl = getImageUrl(thumbnailUrl);
  const hasErrorRef = useRef(false);

  // Reset error ref when image URL changes
  useEffect(() => {
    hasErrorRef.current = false;
  }, [imageUrl]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day';
    if (diffDays < 7) return `${diffDays} days`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks`;
    return `${Math.floor(diffDays / 30)} months`;
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatViews = (views: number) => {
    if (views >= 1000) {
      return `${(views / 1000).toFixed(0)}k`;
    }
    return views.toString();
  };

  // Extract metadata from API response
  const extractMetadata = () => {
    const basicInfo = video.metadata?.basicInfo;
    const ageCategories = basicInfo?.ageCategory || [];
    
    // Extract level from difficultyLevel (from API) or tags, default to Beginner
    const difficultyLevel = (video as any).difficultyLevel;
    const tags = video.tags || [];
    let level = 'Beginner';
    if (difficultyLevel) {
      level = difficultyLevel.charAt(0).toUpperCase() + difficultyLevel.slice(1).toLowerCase();
    } else {
      const tagLevel = tags.find(t => ['beginner', 'intermediate', 'advanced'].includes(t.toLowerCase()));
      if (tagLevel) {
        level = tagLevel.charAt(0).toUpperCase() + tagLevel.slice(1).toLowerCase();
      }
    }
    
    // Format video type/chapter info
    let videoType = 'Video';
    let chapterInfo = '';
    if (video.chapterTitle) {
      videoType = 'Video';
      chapterInfo = video.chapterTitle; // e.g., "Video 1 of series"
    } else if (video.chapterNumber) {
      videoType = 'Video';
      chapterInfo = `Video ${video.chapterNumber} of series`;
    } else {
      chapterInfo = 'Video';
    }
    
    // Extract age range from ageCategory array
    let ageRange = null; // Changed from 'N/A' to null to conditionally render
    if (ageCategories.length > 0) {
      // Format age categories (e.g., ["U7", "U9", "U11"] -> "7 - 11 Years")
      const ages = ageCategories.map((cat: string) => {
        const match = cat.match(/U(\d+)/);
        return match ? parseInt(match[1]) : null;
      }).filter((age: number | null) => age !== null) as number[];
      
      if (ages.length > 0) {
        const minAge = Math.min(...ages);
        const maxAge = Math.max(...ages);
        ageRange = `${minAge} - ${maxAge} Years`;
      }
    }
    
    // Format chapters display - use totalChapters from API if available
    let chaptersDisplay = '0 Chapters';
    if ((video as any).totalChapters !== undefined && (video as any).totalChapters !== null) {
      const totalChapters = (video as any).totalChapters;
      chaptersDisplay = `${totalChapters} ${totalChapters === 1 ? 'Chapter' : 'Chapters'}`;
    } else if (video.chapterTitle && video.chapterTitle.includes('series')) {
      // Extract number from "Video 1 of series" -> "1 Chapters" or keep as is
      chaptersDisplay = video.chapterTitle.replace('Video ', '').replace(' of series', '') + ' Chapters';
    } else if (video.chapterNumber) {
      chaptersDisplay = `${video.chapterNumber} Chapters`;
    }
    
    return { level, videoType, chapterInfo, ageRange, chaptersDisplay };
  };

  const metadata = extractMetadata();
  // Check for duration in multiple possible locations from API response
  const durationValue = video.duration || (video as any).totalDuration || 0;
  const duration = formatDuration(durationValue);
  
  // Get coach name from metadata
  const coachName = video.metadata?.basicInfo?.fullName || 'Test Coach';

  // Determine status label - Published, Draft, or Archived
  // For Level Up series, check status and return appropriate label
  const getStatusLabel = () => {
    const status = video.status?.toLowerCase() || '';
    // Check for archived status first
    if (status === 'archived') {
      return 'Archived';
    }
    // Consider published and live as Published
    if (status === 'published' || status === 'live') {
      return 'Published';
    }
    // All other statuses (draft, pending_review, scheduled, rejected, etc.) show as Draft
    return 'Draft';
  };

  const statusLabel = getStatusLabel();
  
  // Determine content type label - Series or Standalone
  const getContentTypeLabel = () => {
    const contentType = video.contentType?.toLowerCase() || '';
    if (contentType === 'series') {
      return 'Series';
    } else if (contentType === 'long_form') {
      return 'Standalone';
    }
    // Fallback: check videoType if contentType is not available
    if (video.videoType === 'series') {
      return 'Series';
    }
    return 'Standalone'; // Default to Standalone
  };

  const contentTypeLabel = getContentTypeLabel();
  
  // Get content type badge styling matching manage-tournaments-fixtures pattern
  const getContentTypeBadgeStyle = () => {
    const baseStyle = {
      border: '1px solid',
      fontWeight: '500' as const,
      padding: '0.375rem 0.75rem',
      borderRadius: '6px',
      textTransform: 'capitalize' as const,
      lineHeight: '1.2' as const,
      display: 'inline-flex' as const,
      alignItems: 'center' as const,
      verticalAlign: 'middle' as const
    };

    if (contentTypeLabel === 'Series') {
      return {
        ...baseStyle,
        backgroundColor: '#E6F1FF',
        color: '#006EFF',
        borderColor: '#006EFF'
      };
    } else {
      // Standalone
      return {
        ...baseStyle,
        backgroundColor: '#F3E5F5',
        color: '#9C27B0',
        borderColor: '#9C27B0'
      };
    }
  };
  
  // Get status badge styling matching manage-tournaments-fixtures pattern
  const getStatusBadgeStyle = () => {
    const baseStyle = {
      border: '1px solid',
      fontWeight: '500' as const,
      padding: '0.375rem 0.75rem',
      borderRadius: '6px',
      textTransform: 'capitalize' as const,
      lineHeight: '1.2' as const,
      display: 'inline-flex' as const,
      alignItems: 'center' as const,
      verticalAlign: 'middle' as const
    };

    if (statusLabel === 'Published') {
      return {
        ...baseStyle,
        backgroundColor: '#E9FCF2',
        color: '#1B9E5C',
        borderColor: '#1B9E5C'
      };
    } else if (statusLabel === 'Archived') {
      return {
        ...baseStyle,
        backgroundColor: '#F8F9FA',
        color: '#6C757D',
        borderColor: '#6C757D'
      };
    } else {
      // Draft
      return {
        ...baseStyle,
        backgroundColor: '#FFF5EC',
        color: '#FD9644',
        borderColor: '#FD9644'
      };
    }
  };

  const getCategoryPath = (category: string): string => {
    if (category === 'action') return 'action';
    if (category === 'level_up') return 'level_up';
    if (category === 'social') return 'social';
    return 'level_up'; // Default
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on the menu
    if ((e.target as HTMLElement).closest('.levelup-card-menu')) {
      return;
    }
    const categoryPath = getCategoryPath(video.category);
    // Determine if this is a chapter (not a series)
    const isChapter = video.videoType !== 'series';
    
    // Navigate with state to help detail page determine which API to use
    navigate(`/community/${categoryPath}/${video._id}`, {
      state: {
        isChapter: isChapter,
        chapterId: isChapter ? video._id : undefined,
        seriesId: isChapter ? (video as any).seriesId : undefined
      }
    });
  };

  return (
    <div className="levelup-card" onClick={handleCardClick}>
      {/* Thumbnail Section */}
      <div className="levelup-card-thumbnail">
        <img
          src={hasErrorRef.current ? '/placeholder-video.png' : (imageUrl || '/placeholder-video.png')}
          alt={video.title}
          className="levelup-card-image"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            // Only set placeholder if we haven't already tried it
            if (target.src !== window.location.origin + '/placeholder-video.png' && !hasErrorRef.current) {
              hasErrorRef.current = true;
              target.src = '/placeholder-video.png';
            } else {
              // If placeholder also fails, hide the image to prevent infinite loop
              target.style.display = 'none';
            }
          }}
        />
        {/* Play button overlay */}
        <div className="levelup-play-overlay">
          <div className="play-button">
            <img 
              src={getImageUrl('shared/web-admin/common/Play_icon.svg')} 
              alt="Play" 
              className="play-icon-image"
              onError={(e) => {
                // Fallback to Phosphor icon if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallbackIcon = document.createElement('i');
                fallbackIcon.className = 'ph-play-fill';
                target.parentElement?.appendChild(fallbackIcon);
              }}
            />
          </div>
        </div>
        {/* Badges container - top right - both badges side by side */}
        <div className="levelup-badges-container">
          {/* Content type badge - first badge (left) - using payment-badge class for uniformity with fixtures */}
          <span 
            className="badge payment-badge levelup-content-type-badge"
            style={getContentTypeBadgeStyle()}
          >
            {contentTypeLabel}
          </span>
          {/* Status badge - second badge (right) - using payment-badge class for uniformity with fixtures */}
          <span 
            className="badge payment-badge levelup-status-badge"
            style={getStatusBadgeStyle()}
          >
            {statusLabel}
          </span>
        </div>
        {/* Green duration badge */}
        <div className="levelup-duration-badge">
          <i className="ph-clock"></i>
          <span>{duration}</span>
        </div>
      </div>
      
      {/* Details Section */}
      <div className="levelup-card-body">
        <h5 className="levelup-card-title">{video.title}</h5>
        <p className="levelup-card-meta">
          {metadata.level} | {metadata.chaptersDisplay}{metadata.ageRange ? ` | ${metadata.ageRange}` : ''}
        </p>
        <div className="levelup-card-footer">
          <span className="levelup-date">
            <i className="ph-clock"></i>
            {formatDate(video.createdAt)}
          </span>
          <span className="levelup-separator">•</span>
          <span className="levelup-views">
            <i className="ph-eye"></i>
            {formatViews(video.views)} Views
          </span>
          <div className="levelup-card-menu" onClick={(e) => e.stopPropagation()}>
            <CommunityActionMenu
              video={video}
              onEdit={onEdit}
              onPublish={onPublish}
              onUnpublish={onUnpublish}
              onDelete={onDelete}
              isOpen={isMenuOpen}
              onToggle={onToggleMenu}
              onClose={onCloseMenu}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LevelUpCard;

